import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search as SearchIcon, UnfoldMore } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  InputAdornment,
  Popover,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import useLocalStorage from '@/hooks/useLocalStorage'
import useOverlay from '@/hooks/useOverlay'
import { Account } from '@/types/accounts'
import AccountSection from './AccountSection'
import { useCurrentAccountContext } from './CurrentAccountProvider'

const MAX_RECENTS = 5

type AccountPickerPopoverProps = {
  isLite?: boolean
}

type AccountPickerContentProps = {
  accounts: Account[]
  currentAccount: Account
  favoriteIds: string[]
  recentIds: string[]
  onSelect: (account: Account) => void
  onToggleFavorite: (accountId: string) => void
}

function AccountPickerContent({
  accounts,
  currentAccount,
  favoriteIds,
  recentIds,
  onSelect,
  onToggleFavorite,
}: AccountPickerContentProps) {
  const theme = useTheme()
  const [search, setSearch] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const highlightedRef = useRef<HTMLElement | null>(null)

  const searchLower = search.toLowerCase()

  const filteredAccounts = useMemo(
    () =>
      accounts.filter(
        (a) =>
          a.name.toLowerCase().includes(searchLower) ||
          a.id.toLowerCase().includes(searchLower),
      ),
    [accounts, searchLower],
  )

  const sections = useMemo(() => {
    const favorites = accounts
      .filter((a) => favoriteIds.includes(a.id))
      .sort((a, b) => a.name.localeCompare(b.name))

    const recents = recentIds
      .map((id) => accounts.find((a) => a.id === id))
      .filter((a): a is Account => !!a)

    const activeClients = filteredAccounts
      .filter((a) => a.customer_id && a.status === 'active')
      .sort((a, b) => a.name.localeCompare(b.name))

    const activeClientIds = new Set(activeClients.map((a) => a.id))
    const other = filteredAccounts
      .filter((a) => !activeClientIds.has(a.id))
      .sort((a, b) => a.name.localeCompare(b.name))

    return { favorites, recents, activeClients, other }
  }, [accounts, filteredAccounts, favoriteIds, recentIds])

  const flatFilteredList = useMemo(
    () => [...sections.activeClients, ...sections.other],
    [sections.activeClients, sections.other],
  )

  useEffect(() => {
    setHighlightedIndex(0)
  }, [search])

  useEffect(() => {
    highlightedRef.current?.scrollIntoView({ block: 'nearest' })
  }, [highlightedIndex])

  const highlightedAccount =
    flatFilteredList.length > 0
      ? flatFilteredList[
          Math.min(highlightedIndex, flatFilteredList.length - 1)
        ]
      : undefined

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (flatFilteredList.length === 0) return
    if (e.key === 'Enter') {
      e.preventDefault()
      if (highlightedAccount) onSelect(highlightedAccount)
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((prev) =>
        prev < flatFilteredList.length - 1 ? prev + 1 : prev,
      )
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev))
    }
  }

  return (
    <Stack
      direction="row"
      sx={{
        overflow: 'hidden',
        flex: 1,
        minHeight: 0,
      }}
    >
      <Stack
        sx={{
          flex: 1,
          overflow: 'auto',
          minWidth: 0,
          borderRight: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ p: 1.5, pb: 0 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Search accounts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
        <Box sx={{ overflow: 'auto', flex: 1, pb: 1 }}>
          <AccountSection
            title="Active Clients"
            accounts={sections.activeClients}
            currentAccount={currentAccount}
            favoriteIds={favoriteIds}
            onSelect={onSelect}
            onToggleFavorite={onToggleFavorite}
            highlightedAccountId={highlightedAccount?.id}
            highlightedRef={highlightedRef}
          />
          <AccountSection
            title="Other"
            accounts={sections.other}
            currentAccount={currentAccount}
            favoriteIds={favoriteIds}
            onSelect={onSelect}
            onToggleFavorite={onToggleFavorite}
            highlightedAccountId={highlightedAccount?.id}
            highlightedRef={highlightedRef}
          />
          {filteredAccounts.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: 'center', py: 4 }}
            >
              No accounts match your search
            </Typography>
          )}
        </Box>
      </Stack>

      <Stack
        sx={{
          width: 260,
          p: 1.5,
          bgcolor: 'action.hover',
        }}
        spacing={1.5}
      >
        <Card variant="outlined" sx={{ background: 'transparent' }}>
          <AccountSection
            title="Favorites"
            accounts={sections.favorites}
            currentAccount={currentAccount}
            favoriteIds={favoriteIds}
            onSelect={onSelect}
            onToggleFavorite={onToggleFavorite}
            showStars
            emptyMessage={
              sections.favorites.length === 0 && !search
                ? 'Star accounts to pin them here'
                : undefined
            }
          />
        </Card>
        {sections.recents.length > 0 && (
          <Card variant="outlined" sx={{ background: 'transparent' }}>
            <AccountSection
              title="Recents"
              accounts={sections.recents}
              currentAccount={currentAccount}
              favoriteIds={favoriteIds}
              onSelect={onSelect}
              onToggleFavorite={onToggleFavorite}
            />
          </Card>
        )}
      </Stack>
    </Stack>
  )
}

export default function AccountPickerPopover({
  isLite = false,
}: AccountPickerPopoverProps) {
  const navigate = useNavigate()
  const { accounts, currentAccount, setCurrentAccount } =
    useCurrentAccountContext()

  const popover = useOverlay()

  const [favoriteIds, setFavoriteIds] = useLocalStorage<string[]>(
    'account-favorites',
    [],
    false,
  )

  const [recentIds, setRecentIds] = useLocalStorage<string[]>(
    'account-recents',
    [],
    false,
  )

  const toggleFavorite = useCallback(
    (accountId: string) => {
      setFavoriteIds((prev: string[]) =>
        prev.includes(accountId)
          ? prev.filter((id: string) => id !== accountId)
          : [...prev, accountId],
      )
    },
    [setFavoriteIds],
  )

  const addToRecents = useCallback(
    (accountId: string) => {
      setRecentIds((prev: string[]) => {
        const filtered = prev.filter((id: string) => id !== accountId)
        return [accountId, ...filtered].slice(0, MAX_RECENTS)
      })
    },
    [setRecentIds],
  )

  const changeAccount = useCallback(
    (account: Account) => {
      popover.close()
      addToRecents(account.id)
      setCurrentAccount(account.id)
        .then(() => {
          navigate(isLite ? '/lite' : '/projects')
        })
        .catch((e) => {
          console.error('Failed to switch accounts', e)
        })
    },
    [addToRecents, isLite, navigate, setCurrentAccount, popover],
  )

  if (!currentAccount || accounts.length <= 1) {
    return currentAccount ? (
      <Typography sx={{ px: 1 }}>{currentAccount.name}</Typography>
    ) : null
  }

  return (
    <>
      <Button
        onClick={popover.open}
        size="small"
        color="neutral"
        variant="text"
        endIcon={<UnfoldMore fontSize="small" />}
        sx={{
          textTransform: 'none',
        }}
      >
        {currentAccount.name}
      </Button>

      <Popover
        open={popover.isOpen}
        anchorEl={popover.anchorEl as HTMLElement}
        onClose={popover.close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              width: 680,
              maxHeight: 800,
              display: 'flex',
              flexDirection: 'column',
              transition: 'width 0.2s',
            },
          },
        }}
      >
        {popover.isOpen && (
          <AccountPickerContent
            accounts={accounts}
            currentAccount={currentAccount}
            favoriteIds={favoriteIds}
            recentIds={recentIds}
            onSelect={changeAccount}
            onToggleFavorite={toggleFavorite}
          />
        )}
      </Popover>
    </>
  )
}
